import React, { useState } from "react";

import { FeedbackButton } from "@/components/FeedbackButton/FeedbackButton";
import { SPLASH_CONTENT } from "@/components/SplashScreen/SPLASH_CONTENT";
import { SplashScreen } from "@/components/SplashScreen/SplashScreen";
import { groupListLabel } from "@/components/display/GroupDisplayName";
import { SparkleIcon } from "@/components/icons/Sparkle";
import { Button } from "@/components/ui/button";
import { useUserGroupsData } from "@/data/use-groups-data";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeft } from "@nnr/fontawesome-sharp-light/icons/sharp/light";
import { Link, useNavigate } from "@tanstack/react-router";
import { useEffect } from "react";
import { useCountdown } from "usehooks-ts";
import { Column, Row } from "../components/ui/flex";
import { useUserLocalSettings } from "../hooks/use-user-local-settings";

const REDIRECTION_TIMEOUT = 5;

export const UserGroupsPage: React.FC = () => {
  const { groups, isLoading: isLoadingGroups } = useUserGroupsData();
  const { lastGroupUuid } = useUserLocalSettings();
  const [shouldCountdown, setShouldCountdown] = useState(true);
  const [countdown, { startCountdown, stopCountdown }] = useCountdown({
    countStart: REDIRECTION_TIMEOUT,
  });
  const navigate = useNavigate();

  useEffect(() => {
    if (isLoadingGroups) return;
    const lastGroup = groups.find((group) => group.uuid === lastGroupUuid);
    if (!lastGroup) return;

    if (countdown === REDIRECTION_TIMEOUT) {
      startCountdown();
    }
    if (countdown === 0) {
      navigate({
        to: "/w/$groupSlug",
        params: { groupSlug: lastGroup.slug },
      });
    }
  }, [countdown, lastGroupUuid, isLoadingGroups, groups]);

  const handleScreenClick = () => {
    stopCountdown();
    setShouldCountdown(false);
  };

  return (
    <>
      <SplashScreen splashContent={SPLASH_CONTENT} onClick={handleScreenClick}>
        <span className="text-4xl font-bold">Select your workspace</span>

        <div className="min-h-8" />

        <Column align="start" className="gap-1">
          {[...groups]
            .sort((left, right) => {
              if (left.personal !== right.personal) {
                return left.personal ? -1 : 1;
              }
              return left.name.localeCompare(right.name);
            })
            .map((group) => (
              <Link
                to="/w/$groupSlug"
                params={{ groupSlug: group.slug }}
                key={group.uuid}
              >
                <Row className="items-center gap-4">
                  <Button
                    variant={
                      lastGroupUuid === group.uuid ? "default" : "outline"
                    }
                    className="font-bold"
                  >
                    {lastGroupUuid === group.uuid ? <SparkleIcon /> : null}

                    {groupListLabel(group)}
                    <span className="text-xs font-normal opacity-70">
                      {group.organizationName}
                    </span>
                  </Button>
                  {lastGroupUuid === group.uuid && (
                    <>
                      <FontAwesomeIcon icon={faArrowLeft} />
                      <Column className="gap-0.5 text-xs">
                        <div className="font-bold">LAST VISITED</div>
                        <div>
                          {shouldCountdown ? `Redirecting in ${countdown}` : ""}
                        </div>
                      </Column>
                    </>
                  )}
                </Row>
              </Link>
            ))}
        </Column>
      </SplashScreen>

      <FeedbackButton className="absolute right-2 bottom-2" />
    </>
  );
};
